import {sportingSummary} from "@/components/MatchSportingConfig";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate, useParams, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import AttachmentsContainer from "@/components/AttachmentsContainer";
import MatchScouting from "@/components/MatchScouting";
import TacticalBoardPanel from "@/components/TacticalBoardPanel";
import Breadcrumbs from "@/components/Breadcrumbs";
import GpsStatsTab from "@/pages/Manager/Match/GpsStatsTab";
import Loading from "@/components/Loading";
import MainActions from "@/components/MainActions";
import ResultStatus from "@/pages/Manager/Match/ResultStatus";
import PlayerStatsTab from "@/pages/Manager/Match/PlayerStatsTab";
import MatchVideoTagging from "@/pages/Manager/Match/MatchVideoTagging";
import NotesSection from "@/components/NotesSection";
import Page from "@/components/Page";
import Rating from "@/components/Rating";
import SoccerField from "@/components/SoccerField";
import Tabs from "@/components/Tabs";
import VLFCard from "@/components/Card";
import VLFModal from "@/components/Modal";
import {useInjuryConfirmation} from "@/utils/hooks";
import VLFSelectNew from "@/components/Input/SelectNew";
import HandleAttendanceModal from "@/pages/Manager/Match/handleAttendance";
import ConvocationsTab from "@/pages/Manager/Match/ConvocationsTab";
import DistinteTab, {MatchChronology} from "@/pages/Manager/Match/DistinteTab";
import HandleEventModal from "@/pages/Manager/Match/handleEvent";
import HandleReportModal from "@/pages/Manager/Match/handleReport";
import HandleRPEModal from "@/pages/Manager/Match/handleRPE";
import HandleStatModal from "@/pages/Manager/Match/handleStat";
import RpeTab from "@/pages/Manager/Match/RpeTab";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";

import {AttachmentAPI} from "@/api/attachment";
import {ClubAPI} from "@/api/club";
import {MatchAPI} from "@/api/match";
import {MatchReportAPI} from "@/api/match_report";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import {setMatchTab} from "@/actions";

import {
	buildAdvancedStatsPayload,
	createAdvancedStatsInitialValues,
} from "@/config/matchAdvancedStats";

import {
	attendanceStatusOptions,
	dateFormat,
	modulesOptions,
	rpeOptions,
	ERROR_OPERATION,
	getInternalClubId,
	INTERNAL_PLAYER_STATUSES,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment, getTextAreaField} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";
import {generateMatchPDF} from "@/utils/generateMatchPDF";
import {generateMatchLineupPDF} from "@/utils/generateMatchLineupPDF";
import {
	hasConvocationInfo,
	loadConvocationInfo,
	staffMember,
} from "@/utils/convocation";

import AdvancedStatsCards from "@/components/AdvancedStatsCards";

import "./index.scss";

// La scheda è divisa per momento della gara; ogni sezione resta un tab interno.
const MATCH_MOMENTS = [
	{
		key: "before",
		label: "Prima",
		sections: ["match_convocated_players", "tactical_board"],
	},
	{
		key: "match",
		label: "Partita",
		sections: ["main_info", "match_report_players"],
	},
	{
		key: "after",
		label: "Dopo",
		sections: ["analysis", "advanced_stats", "stats"],
	},
	{key: "load", label: "Carico", sections: ["rpe", "gps"]},
	{key: "scouting", label: "Scouting", sections: ["scouting"]},
];

function MatchMoment({sections, current, onChange}) {
	const section = sections.find((s) => s.key === current) || sections[0];
	return (
		<>
			{sections.length > 1 && (
				<div className="match-moment__switch">
					<ButtonGroup disableElevation size="small" aria-label="Sezione">
						{sections.map((s) => (
							<Button
								key={s.key}
								variant={s.key === section.key ? "contained" : "outlined"}
								onClick={() => onChange(s.key)}
							>
								{s.label}
							</Button>
						))}
					</ButtonGroup>
				</div>
			)}
			{section.render()}
		</>
	);
}

function MatchEmpty({text, action}) {
	return (
		<div className="match-empty">
			<p>{text}</p>
			{action && <MainActions actions={[action]} />}
		</div>
	);
}

function MatchPage(props) {
	const {handleNotification, scoutingContext = false} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const basePath = "/matches";
	const location = useLocation();
	const returnTo =
		location.state?.returnTo ||
		(scoutingContext ? "/scouting/planning" : "/matches");
	useEffect(() => {
		dispatch(
			setMatchTab(
				location.state?.initialTab ||
					(scoutingContext ? "scouting" : "main_info"),
			),
		);
	}, [id, scoutingContext, dispatch]);

	const {categories} = useCategories({
		handleNotification: undefined,
		setLoading: false,
	});

	const currentTab = useSelector((state) => state.matchTab);

	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = useSelector((state) => state?.user?.playerId);

	const userRole = useSelector((state) => state?.user?.roleName);

	const [loading, setLoading] = useState(false);
	const [attachments, setAttachments] = useState([]);
	const [selectedBoardId, setSelectedBoardId] = useState(null);
	useEffect(() => {
		setSelectedBoardId(null);
	}, [id]);
	const editTacticalBoard = (boardId) => {
		setSelectedBoardId(boardId);
		dispatch(setMatchTab("tactical_board"));
	};
	const [attendance, setAttendance] = useState("");
	const [clubs, setClubs] = useState([]);
	const [clubsLoaded, setClubsLoaded] = useState(false);
	const [matchData, setMatchData] = useState({});
	const competitionEditionId =
		matchData.competitionSeasonId || matchData.competitionSeason?.id;
	const competitionLabel = matchData.competitionSeason
		? (matchData.competitionSeason.competition?.name ||
				matchData.competitionName ||
				"Competizione") +
			" — " +
			(matchData.competitionSeason.season?.name || "")
		: matchData.competitionName || "Apri competizione";
	const competitionLink = competitionEditionId ? (
		<Link
			className="match-competition-link"
			to={"/competitions/" + competitionEditionId}
			title="Apri la competizione"
		>
			{competitionLabel}
		</Link>
	) : null;
	const [matchNotes, setMatchNotes] = useState([]);
	const [prospectPlayers, setProspectPlayers] = useState([]);
	const [prospectPlayersLoaded, setProspectPlayersLoaded] = useState(false);
	const [teamPlayers, setTeamPlayers] = useState([]);
	const [teamPlayersLoaded, setTeamPlayersLoaded] = useState(false);
	const catalogsInFlight = useRef(new Set());

	const [entityToDelete, setEntityToDelete] = useState({});
	const [formData, setFormData] = useState({});
	const [initialData, setInitialData] = useState();

	const [openAttendanceModal, setOpenAttendanceModal] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openEventModal, setOpenEventModal] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [openReportModal, setOpenReportModal] = useState(false);
	const [openRPEModal, setOpenRPEModal] = useState(false);

	const [ourTacticalSystem1stHalf, setOurTacticalSystem1stHalf] = useState();
	const [opponentTacticalSystem1stHalf, setOpponentTacticalSystem1stHalf] =
		useState();
	const [ourTacticalSystem2ndHalf, setOurTacticalSystem2ndHalf] = useState();
	const [opponentTacticalSystem2ndHalf, setOpponentTacticalSystem2ndHalf] =
		useState();
	const [analysisEditing, setAnalysisEditing] = useState(false);
	const [analysisFields, setAnalysisFields] = useState({});
	const [isAnalysisSaved, setIsAnalysisSaved] = useState(false);
	const [advancedStats, setAdvancedStats] = useState(null);
	const [advancedStatsLoaded, setAdvancedStatsLoaded] = useState(false);
	const [loadingAdvancedStats, setLoadingAdvancedStats] = useState(false);
	const [analysisLoaded, setAnalysisLoaded] = useState(false);
	const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
	const [notesLoaded, setNotesLoaded] = useState(false);
	const [matchRosterPlayerIds, setMatchRosterPlayerIds] = useState(new Set());
	const [matchRosterPlayersLoaded, setMatchRosterPlayersLoaded] =
		useState(false);

	const openGoalkeeperReport = async (reportPlayerId) => {
		setLoading(true);
		try {
			const reports =
				(await MatchReportAPI.list({matchId: id, playerId: reportPlayerId})) ||
				[];
			if (reports.length > 1) {
				handleNotification(
					"Sono presenti più report per questo portiere: scegli quello da modificare nell’elenco Report portieri.",
					"warning",
				);
				return;
			}
			navigate(
				reports.length
					? "/match-reports/" + reports[0].id + "/edit"
					: "/match-reports/new?" +
							new URLSearchParams({matchId: id, playerId: reportPlayerId}),
			);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getMatch = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getById(id, {seasonId});
			try {
				response.playerStatistics = await MatchAPI.getPlayerStatistics(id);
			} catch {
				response.playerStatistics = null;
			}
			setMatchData(response);

			if (userRole === "Player") {
				const playerMeasurement =
					response.matchRpe?.filter((ps) => ps.playerId === playerId)?.[0] ||
					{};
				setFormData({
					...Object.assign({}, playerMeasurement),
					rpe: parseInt(playerMeasurement.rpe),
				});

				const playerConvocation = response.matchConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				setAttendance(playerConvocation ? playerConvocation.status : undefined);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getClubs = async () => {
		if (catalogsInFlight.current.has("clubs")) return;
		catalogsInFlight.current.add("clubs");
		setLoading(true);
		try {
			const response = await ClubAPI.list();
			setClubs(response);
			setClubsLoaded(true);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			catalogsInFlight.current.delete("clubs");
			setLoading(false);
		}
	};

	const getTeamPlayers = async (prospect = true) => {
		const requestKey = "players:" + seasonId + ":" + prospect;
		if (catalogsInFlight.current.has(requestKey)) return;
		catalogsInFlight.current.add(requestKey);
		setLoading(true);
		try {
			const response = await PlayerAPI.list({prospect, seasonId});
			if (!prospect) {
				setTeamPlayers(
					response?.filter(
						(p) =>
							p.isInternal === true ||
							(p.isInternal == null &&
								INTERNAL_PLAYER_STATUSES.includes(p.status)),
					),
				);
				setTeamPlayersLoaded(true);
			} else {
				setProspectPlayers(response);
				setProspectPlayersLoaded(true);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			catalogsInFlight.current.delete(requestKey);
			setLoading(false);
		}
	};

	const resetAnalysis = () => {
		setAnalysisFields({});
		setIsAnalysisSaved(false);
		setAnalysisEditing(false);
		setOurTacticalSystem1stHalf(undefined);
		setOpponentTacticalSystem1stHalf(undefined);
		setOurTacticalSystem2ndHalf(undefined);
		setOpponentTacticalSystem2ndHalf(undefined);
	};

	const getAnalysis = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getAnalysisById(id);
			setOurTacticalSystem1stHalf({
				value: response.ourTacticalSystem1stHalf,
				label: response.ourTacticalSystem1stHalf,
			});
			setOpponentTacticalSystem1stHalf({
				value: response.opponentTacticalSystem1stHalf,
				label: response.opponentTacticalSystem1stHalf,
			});
			setOurTacticalSystem2ndHalf({
				value: response.ourTacticalSystem2ndHalf,
				label: response.ourTacticalSystem2ndHalf,
			});
			setOpponentTacticalSystem2ndHalf({
				value: response.opponentTacticalSystem2ndHalf,
				label: response.opponentTacticalSystem2ndHalf,
			});
			setAnalysisFields(response);
			setAnalysisEditing(false);
			setIsAnalysisSaved(true);
		} catch (error) {
			if (error?.response?.status === 404) {
				resetAnalysis();
			} else {
				handleNotification(ERROR_OPERATION, "error");
			}
		} finally {
			setLoading(false);
			setAnalysisLoaded(true);
		}
	};

	// Statistiche di squadra modificabili nel tab, senza pagina a parte
	const [advancedDraft, setAdvancedDraft] = useState(null);
	const startAdvancedStats = () =>
		setAdvancedDraft(createAdvancedStatsInitialValues(advancedStats || {}));
	const saveAdvancedStats = async () => {
		setLoading(true);
		try {
			await MatchAPI.updateAdvancedStats(
				id,
				buildAdvancedStatsPayload(advancedDraft),
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setAdvancedDraft(null);
			getAdvancedStatsDetails();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getAdvancedStatsDetails = async () => {
		setLoadingAdvancedStats(true);
		try {
			const response = await MatchAPI.getAdvancedStats(id);
			setAdvancedStats(response);
		} catch (error) {
			if (error?.response?.status !== 404) {
				handleNotification(ERROR_OPERATION, "error");
			} else {
				setAdvancedStats(null);
			}
		} finally {
			setLoadingAdvancedStats(false);
			setAdvancedStatsLoaded(true);
		}
	};

	const getMatchAttachment = async () => {
		try {
			const response = await AttachmentAPI.list("match", id);
			setAttachments(response || []);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setAttachmentsLoaded(true);
		}
	};

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setInternalTeams(
				(response || []).filter(
					(r) => !r.clubId || r.clubId === getInternalClubId(),
				),
			);
		} catch {
			// silently ignore
		}
	};

	// Giocatori della/e rosa/e interna/e legata/e alla partita (per distinguerli
	// dagli altri interni convocabili). Il lato interno è home o away a seconda
	// di quale club è quello interno.
	const getMatchRosterPlayers = async () => {
		const rosterIds = [];
		if (
			matchData?.homeClubId === getInternalClubId() &&
			matchData?.homeRosterId
		)
			rosterIds.push(matchData.homeRosterId);
		if (
			matchData?.awayClubId === getInternalClubId() &&
			matchData?.awayRosterId
		)
			rosterIds.push(matchData.awayRosterId);
		if (!rosterIds.length && matchData?.rosterId)
			rosterIds.push(matchData.rosterId);

		if (!rosterIds.length) {
			setMatchRosterPlayerIds(new Set());
			setMatchRosterPlayersLoaded(true);
			return;
		}
		try {
			const responses = await Promise.all(
				rosterIds.map((rid) => RosterAPI.getPlayers(rid).catch(() => [])),
			);
			const ids = new Set();
			responses.flat().forEach((rp) => {
				if (rp?.player?.id) ids.add(rp.player.id);
			});
			setMatchRosterPlayerIds(ids);
		} catch {
			setMatchRosterPlayerIds(new Set());
		} finally {
			setMatchRosterPlayersLoaded(true);
		}
	};

	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// Infortuni attivi alla data della gara, per segnare chi non è convocabile.
	const [injuredIds, setInjuredIds] = useState(null);
	useEffect(() => {
		if (
			currentTab !== "match_convocated_players" ||
			!matchData?.startDate ||
			!seasonId
		)
			return;
		let active = true;
		MedicalAPI.recordsList({seasonId})
			.then((records) => {
				if (!active) return;
				setInjuredIds(
					new Set(
						(records || [])
							.filter((r) =>
								(r.injuriesIllness || []).some((inj) =>
									isInjuryActiveAt(inj, matchData.startDate),
								),
							)
							.map((r) => r.playerId),
					),
				);
			})
			// Senza accesso all'area medica le convocazioni restano manuali.
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [currentTab, matchData?.startDate, seasonId]);

	const {injuryWarning, resolveInjuryWarning, confirmHealthyAttendance} =
		useInjuryConfirmation({
			date: matchData?.startDate,
			handleNotification,
		});

	const handleAttendance = async (data, status, type) => {
		setLoading(true);
		try {
			const selected = type === "multiple" ? data.player : [data.player];
			const body = (selected || []).map((p) => ({
				matchId: matchData.id,
				matchSide: initialData.matchSide,
				playerId: p.value,
				rosterId: p.sourceRosterId,
				status,
			}));
			if (!body.length) return false;
			if (!(await confirmHealthyAttendance(body))) return false;
			type === "multiple"
				? await MatchAPI.createMultipleAttendances(body)
				: await MatchAPI.createAttendance(body[0]);
			await getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
			return true;
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const handlePlayerAttendance = async (data, attendanceId) => {
		setLoading(true);
		try {
			if (!(await confirmHealthyAttendance(data))) return;
			attendanceId
				? await MatchAPI.updateAttendance(id, attendanceId, data)
				: await MatchAPI.createAttendance(data);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const confirmRosterPlayers = async (col, players) => {
		setLoading(true);
		try {
			if (col.clubId === getInternalClubId()) {
				const presences = players.filter(
					(p) =>
						!matchData?.matchConvocations?.some(
							(c) =>
								c.playerId === p.playerId &&
								c.matchSide === col.side &&
								c.status === "Presente",
						),
				);
				if (
					!(await confirmHealthyAttendance(
						presences.map((p) => ({playerId: p.playerId, status: "Presente"})),
					))
				)
					return false;
			}
			await MatchAPI.addPlayers(
				players.map((p) => ({
					...p,
					matchId: id,
					clubId: col.clubId,
					matchSide: col.side,
				})),
			);
			await getMatch();
			handleNotification(SUCCESSFUL_OPERATION, "success");
			return true;
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
			return false;
		} finally {
			setLoading(false);
		}
	};

	const handleReport = async (data) => {
		setLoading(true);
		try {
			if (initialData?.linkParticipationId) {
				const row = data?.[0];
				await MatchAPI.updatePlayer(id, initialData.linkParticipationId, {
					...row,
					id: initialData.linkParticipationId,
				});
				await getMatch();
				handleClose();
				handleNotification(SUCCESSFUL_OPERATION, "success");
				return;
			}

			const body = data?.map((p) => {
				return {
					matchId: matchData?.id,
					clubId: initialData?.clubId,
					matchSide: initialData?.matchSide,
					...p,
				};
			});

			if (initialData?.isInternal) {
				const newPresences = (data ?? [])
					.filter(
						(p) =>
							!matchData?.matchConvocations?.some(
								(c) =>
									c.playerId === p.playerId &&
									c.matchSide === initialData?.matchSide &&
									c.status === "Presente",
							),
					)
					.map((p) => ({playerId: p.playerId, status: "Presente"}));
				if (!(await confirmHealthyAttendance(newPresences))) return;
			}

			await MatchAPI.addPlayers(body);
			getMatch();
			getTeamPlayers();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleEvent = async (data) => {
		setLoading(true);
		try {
			const body = {
				...data,
				matchId: matchData?.id,
				clubId: initialData?.clubId,
				matchSide: initialData?.matchSide,
				playerId: data.player?.playerId ?? null,
				scorerParticipationId: data.player?.provisional
					? data.player.participationId
					: null,
				opponentNumber: data.player?.provisional
					? data.player.playerNumber
					: data.opponentNumber === "" || data.opponentNumber == null
						? null
						: Number(data.opponentNumber),
				opponentName: data.opponentName?.trim() || null,
				playerNumber: data.player?.playerNumber,
				substitutePlayerId: data.substitutePlayer?.playerId ?? null,
				outgoingParticipationId: data.substitutePlayer?.provisional
					? data.substitutePlayer.participationId
					: null,
				opponentOutgoingNumber: data.substitutePlayer?.provisional
					? data.substitutePlayer.playerNumber
					: data.opponentOutgoingNumber === "" ||
						  data.opponentOutgoingNumber == null
						? null
						: Number(data.opponentOutgoingNumber),
				opponentOutgoingName: data.opponentOutgoingName?.trim() || null,
				substitutionReason:
					data.substitutionReason === "injury" ? "injury" : null,
				assistPlayerId: data.assistPlayer?.playerId ?? null,
				assistParticipationId: data.assistPlayer?.provisional
					? data.assistPlayer.participationId
					: null,
				opponentAssistNumber: data.assistPlayer?.provisional
					? data.assistPlayer.playerNumber
					: data.opponentAssistNumber === "" ||
						  data.opponentAssistNumber == null
						? null
						: Number(data.opponentAssistNumber),
				opponentAssistName: data.opponentAssistName?.trim() || null,
				substitutePlayerNumber: data.substitutePlayer?.playerNumber,
				eventType: data?.eventType?.value || initialData?.type,
			};
			delete body.player;
			delete body.assistPlayer;
			delete body.substitutePlayer;
			if (initialData?.event)
				await MatchAPI.updateEvent(id, initialData.event.id, {
					...body,
					id: initialData.event.id,
				});
			else await MatchAPI.addEvents(body);
			getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleAnalysis = async () => {
		setLoading(true);

		const body = {
			...analysisFields,
			ourTacticalSystem1stHalf: ourTacticalSystem1stHalf?.value,
			opponentTacticalSystem1stHalf: opponentTacticalSystem1stHalf?.value,
			ourTacticalSystem2ndHalf: ourTacticalSystem2ndHalf?.value,
			opponentTacticalSystem2ndHalf: opponentTacticalSystem2ndHalf?.value,
			matchId: id,
		};

		try {
			isAnalysisSaved
				? await MatchAPI.updateAnalysis(id, body)
				: await MatchAPI.createAnalysis(body);
			await getAnalysis();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleMeasurement = async (data, type, id) => {
		setLoading(true);
		try {
			if (data.rpe) {
				const rpe =
					userRole === "Player"
						? data.rpe || undefined
						: data.rpe.value || undefined;

				const playerMeasurement = playerId
					? matchData.matchRpe?.filter((ps) => ps.playerId === playerId)?.[0]
					: {};

				const body = {
					id: userRole === "Player" ? playerMeasurement?.id : id,
					matchId: matchData?.id,
					playerId: userRole === "Player" ? playerId : data.player.value,
					...data,
					rpe,
				};
				delete body.player;

				playerMeasurement?.id || id
					? await MatchAPI.updateMeasurement(
							matchData?.id,
							playerMeasurement?.id || id,
							body,
						)
					: await MatchAPI.createMeasurement(body);
			}

			if (type === "multiple") {
				const body = [];
				Object.keys(data?.players)?.forEach((pk) => {
					if (data.players[pk]?.rpe?.value) {
						body.push({
							matchId: matchData?.id,
							playerId: data.players[pk]?.player?.value,
							rpe: data.players[pk]?.rpe?.value,
						});
					}
				});

				await MatchAPI.createMeasurements(body);
			}

			if (userRole === "Player") {
				const playerConvocation = matchData.matchConvocations?.filter(
					(c) => c.playerId === playerId,
				)?.[0];

				await handlePlayerAttendance(
					{
						id: playerConvocation?.id,
						matchId: matchData?.id,
						playerId,
						status: data.rpe ? attendanceStatusOptions[0].label : attendance,
					},
					playerConvocation?.id,
				);
				navigate(basePath);
			} else {
				getMatch();
				handleClose();
			}
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteMatch = async () => {
		setLoading(true);
		try {
			await MatchAPI.delete(matchData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(basePath);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(entityToDelete?.id);
			if (selectedBoardId === entityToDelete?.id) setSelectedBoardId(null);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatchAttachment();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteAttendance = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteAttendance(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// Inline convocations handler — called per-row without full-page loading
	const handleInlineAttendance = async (
		playerId,
		newStatus,
		attendanceId,
		matchSide,
		rosterId,
	) => {
		try {
			if (!(await confirmHealthyAttendance({playerId, status: newStatus})))
				return;
			if (newStatus === null) {
				// Clicking the active status again → remove
				await MatchAPI.deleteAttendance(matchData?.id, attendanceId);
			} else if (attendanceId) {
				await MatchAPI.updateAttendance(matchData?.id, attendanceId, {
					id: attendanceId,
					matchId: matchData?.id,
					matchSide,
					rosterId,
					playerId,
					status: newStatus,
				});
			} else {
				await MatchAPI.createAttendance({
					id: attendanceId,
					matchId: matchData?.id,
					matchSide,
					rosterId,
					playerId,
					status: newStatus,
				});
			}
			// Refresh only matchConvocations — reuse getMatch which is already lightweight
			getMatch();
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		}
	};

	const deleteMatchPlayer = async () => {
		setLoading(true);
		try {
			await MatchAPI.deletePlayer(id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const deleteMatchEvent = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteEvent(id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const deleteMeasurement = async () => {
		setLoading(true);
		try {
			await MatchAPI.deleteMeasurement(matchData?.id, entityToDelete?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setOpenModal(false);
		setOpenAttendanceModal(false);
		setOpenReportModal(false);
		setOpenEventModal(false);
		setOpenRPEModal(false);
		setInitialData();
	};

	useEffect(() => {
		getMatch();
		setAdvancedStats(null);
		setAdvancedStatsLoaded(false);
		setAdvancedDraft(null);
		setClubsLoaded(false);
		setTeamPlayersLoaded(false);
		setProspectPlayersLoaded(false);
		resetAnalysis();
		setAnalysisLoaded(false);
		setAttachmentsLoaded(false);
		setNotesLoaded(false);
		setMatchRosterPlayerIds(new Set());
		setMatchRosterPlayersLoaded(false);
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (currentTab === "match_convocated_players") {
			if (!clubsLoaded) getClubs();
			if (!teamPlayersLoaded) getTeamPlayers(false);
		}

		if (currentTab === "rpe" && !matchRosterPlayersLoaded && matchData?.id) {
			getMatchRosterPlayers();
		}

		if (openReportModal) {
			if (initialData?.isInternal) {
				if (!teamPlayersLoaded) getTeamPlayers(false);
			} else if (!prospectPlayersLoaded) getTeamPlayers();
		}

		if (
			["main_info", "tactical_board"].includes(currentTab) &&
			userRole !== "Player"
		) {
			if (!attachmentsLoaded) getMatchAttachment();
			if (currentTab === "main_info" && !notesLoaded) getMatchNotes();
		}

		if (currentTab === "analysis" && !analysisLoaded) {
			getAnalysis();
		}

		if (currentTab === "gps") {
			if (!teamPlayersLoaded) getTeamPlayers(false);
		}

		if (currentTab === "rpe" || currentTab === "stats") {
			if (!teamPlayersLoaded) getTeamPlayers(false);
		}

		if (currentTab === "advanced_stats" && !advancedStatsLoaded) {
			getAdvancedStatsDetails();
		}
	}, [
		currentTab,
		openReportModal,
		initialData?.isInternal,
		advancedStatsLoaded,
		clubsLoaded,
		teamPlayersLoaded,
		prospectPlayersLoaded,
		analysisLoaded,
		attachmentsLoaded,
		notesLoaded,
		matchRosterPlayersLoaded,
		matchData?.id,
	]); // eslint-disable-line react-hooks/exhaustive-deps

	const saveMatchPlayer = async (player, changes) => {
		try {
			await MatchAPI.updatePlayer(id, player.id, {id: player.id, ...changes});
			await getMatch();
			handleNotification(SUCCESSFUL_OPERATION, "success");
			return {success: true};
		} catch (error) {
			return {
				success: false,
				error: error?.response?.data?.error || ERROR_OPERATION,
			};
		}
	};

	const handleMatchPlayerUpdate = async (p) => {
		setLoading(true);
		try {
			const body = {
				...p,
				startingPlayer: !p.startingPlayer,
			};

			await MatchAPI.updatePlayer(id, p.id, body);
			getMatch();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	// --------------------------------------------------------------------------------

	const getMatchNotes = async () => {
		try {
			const response = await MatchAPI.getNotes(id, {seasonId});
			setMatchNotes(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setNotesLoaded(true);
		}
	};

	const handleStat = async (data, id) => {
		setLoading(true);
		try {
			await MatchAPI.savePlayerStatistics(matchData.id, data.participationId, {
				values: data.values,
			});
			await getMatch();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error?.response?.data?.error || ERROR_OPERATION,
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const deleteEvaluation = async () => {
		setLoading(true);
		try {
			await MatchAPI.deletePlayerStatistics(
				matchData?.id,
				entityToDelete?.participationId,
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getMatch();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleNote = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				date: data?.date || moment().format("YYYY-MM-DD"),
				matchId: matchData?.id,
				...data,
			};
			delete body.player;

			id
				? await MatchAPI.updateNote(matchData?.id, id, body)
				: await MatchAPI.createNote(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteNote = async (id) => {
		setLoading(true);
		try {
			await MatchAPI.deleteNote(matchData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getMatchNotes();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadLineupPDF = async (team) => {
		if (!matchData?.id || !team?.clubId) {
			return;
		}

		try {
			const clubPlayers = (matchData.matchPlayers || []).filter((p) =>
				p.matchSide
					? p.matchSide === team.side
					: p.clubId === team.clubId &&
						matchData.homeClubId !== matchData.awayClubId,
			);
			// Staff in panchina: quello scelto nella convocazione, altrimenti righe da compilare a mano
			const staffIds = hasConvocationInfo(matchData, team.side)
				? loadConvocationInfo(matchData, team.side, team.rosterId).staffIds
				: [];
			const roster =
				team.rosterId && staffIds.length
					? await RosterAPI.getById(team.rosterId).catch(() => null)
					: null;
			generateMatchLineupPDF(matchData, {
				...team,
				players: clubPlayers,
				staff: (roster?.staff || [])
					.map(staffMember)
					.filter((s) => staffIds.includes(s.id)),
				roundLabel: matchData?.competitionSeasonGroupRoundName || "",
			});
			handleNotification("PDF generato con successo", "success");
		} catch {
			handleNotification("Errore nella generazione del PDF", "error");
		}
	};

	// gestire per ogni tab
	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(returnTo),
			color: "info",
		},
	];

	const isInternalMatch =
		!matchData?.id ||
		matchData?.homeClubId === getInternalClubId() ||
		matchData?.awayClubId === getInternalClubId();

	if (userRole !== "Player" && matchData?.id) {
		mainActions.push(
			{
				icon: <EditIcon />,
				title: "Modifica",
				onClick: () =>
					navigate(basePath + "/" + matchData?.id + "/edit", {
						state: {returnTo},
					}),
			},
			{
				icon: <PictureAsPdfIcon />,
				title: "PDF",
				variant: "outlined",
				onClick: () => {
					try {
						generateMatchPDF(
							{
								...matchData,
								resolvedRound: matchData?.competitionSeasonGroupRoundName || "",
							},
							teamPlayers,
							t,
						);
					} catch {
						handleNotification("PDF non disponibile, riprova", "error");
					}
				},
			},
		);
		if (isInternalMatch) {
			mainActions.push({
				icon: <DeleteIcon />,
				title: "Elimina",
				variant: "outlined",
				color: "error",
				onClick: () => {
					setEntityToDelete({type: "match", id});
					setOpenHandleConfirm(true);
				},
			});
		}
	}

	if (isInternalMatch && currentTab === "rpe" && userRole === "Player") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: () => handleMeasurement(formData, formData?.id),
			disabled: !formData.rpe && !attendance,
		});
	}

	// Azioni del tab aperto: dentro il tab, con il testo visibile
	const tabActions = [];

	if (isInternalMatch && currentTab === "rpe" && userRole !== "Player") {
		tabActions.push({
			icon: <AddIcon />,
			label: "Aggiungi RPE",
			onClick: () => {
				setInitialData({});
				setOpenRPEModal(true);
			},
		});
	}

	if (matchData.canEditMatch && currentTab === "analysis") {
		if (!analysisEditing && isAnalysisSaved) {
			tabActions.push({
				icon: <EditIcon />,
				label: "Modifica analisi",
				onClick: () => setAnalysisEditing(true),
			});
		} else if (analysisEditing) {
			tabActions.push(
				{
					icon: <SaveIcon />,
					label: "Salva",
					onClick: () => handleAnalysis(),
				},
				{
					icon: <CloseIcon />,
					label: "Annulla",
					variant: "outlined",
					onClick: () => {
						setAnalysisEditing(false);
					},
				},
			);
		}
	}

	if (
		matchData.canEditMatch &&
		currentTab === "advanced_stats" &&
		userRole !== "Player" &&
		advancedStatsLoaded
	) {
		tabActions.push(
			...(advancedDraft
				? [
						{
							icon: <SaveIcon />,
							label: "Salva",
							onClick: saveAdvancedStats,
						},
						{
							icon: <CloseIcon />,
							label: "Annulla",
							variant: "outlined",
							onClick: () => setAdvancedDraft(null),
						},
					]
				: advancedStats
					? [
							{
								icon: <EditIcon />,
								label: "Modifica statistiche",
								onClick: startAdvancedStats,
							},
						]
					: []),
		);
	}

	const breadcrumbsElements = [
		userRole === "Player"
			? {label: "Partite", path: basePath}
			: {label: "Calendario", path: "/calendar"},
		{label: "/"},
	];

	const isMatchCategoryPro =
		categories?.filter((c) => c.id === matchData?.playersCategoryId)?.[0]
			?.basicActivities !== true;

	const getTacticalSystem = (system) => {
		return system
			? {
					df: system?.value?.split("-")?.[0],
					cm: system?.value?.split("-")?.[1],
					fw: system?.value?.split("-")?.[2],
				}
			: null;
	};

	const getFractionField = (first = true) => {
		return (
			<div className="match-lineups-container">
				<div className="lineup-container">
					<div className="analysis-team-header">
						<div className="analysis-team-identity">
							<span className="analysis-team-label">
								{matchData?.away ? "Trasferta" : "Casa"}
							</span>
							<div className="title">
								{matchData?.away ? matchData?.awayClub : matchData?.homeClub}
							</div>
						</div>
						<div className="analysis-system">
							<VLFSelectNew
								options={modulesOptions}
								value={
									first
										? analysisEditing
											? ourTacticalSystem1stHalf
											: ourTacticalSystem1stHalf?.value
										: analysisEditing
											? ourTacticalSystem2ndHalf
											: ourTacticalSystem2ndHalf?.value
								}
								onChange={(newValue) => {
									first
										? setOurTacticalSystem1stHalf(newValue)
										: setOurTacticalSystem2ndHalf(newValue);
								}}
								noMargin
								label="Sistema di gioco"
								editable={analysisEditing}
							/>
						</div>
					</div>
					<div className="field">
						{getTextAreaField({
							values: analysisFields,
							setValues: setAnalysisFields,
							fieldKey: first ? "ourNotes1stHalf" : "ourNotes2ndHalf",
							label: "Note",
							minRows: 4,
							maxRows: 10,
							editable: analysisEditing,
						})}
					</div>
				</div>
				<div className="field-container">
					<SoccerField
						homeModule={getTacticalSystem(
							first ? ourTacticalSystem1stHalf : ourTacticalSystem2ndHalf,
						)}
						awayModule={getTacticalSystem(
							first
								? opponentTacticalSystem1stHalf
								: opponentTacticalSystem2ndHalf,
						)}
					/>
				</div>
				<div className="lineup-container">
					<div className="analysis-team-header">
						<div className="analysis-team-identity">
							<span className="analysis-team-label">
								{matchData?.away ? "Casa" : "Trasferta"}
							</span>
							<div className="title">
								{matchData?.away ? matchData?.homeClub : matchData?.awayClub}
							</div>
						</div>
						<div className="analysis-system">
							<VLFSelectNew
								options={modulesOptions}
								value={
									first
										? analysisEditing
											? opponentTacticalSystem1stHalf
											: opponentTacticalSystem1stHalf?.value
										: analysisEditing
											? opponentTacticalSystem2ndHalf
											: opponentTacticalSystem2ndHalf?.value
								}
								onChange={(newValue) => {
									first
										? setOpponentTacticalSystem1stHalf(newValue)
										: setOpponentTacticalSystem2ndHalf(newValue);
								}}
								noMargin
								label="Sistema di gioco"
								editable={analysisEditing}
							/>
						</div>
					</div>
					<div className="field">
						{getTextAreaField({
							values: analysisFields,
							setValues: setAnalysisFields,
							fieldKey: first ? "opponentNotes1stHalf" : "opponentNotes2ndHalf",
							label: "Note",
							minRows: 4,
							maxRows: 10,
							editable: analysisEditing,
						})}
					</div>
				</div>
			</div>
		);
	};

	const renderAdvancedStatsTab = () => {
		if (!advancedStatsLoaded && loadingAdvancedStats) {
			return null;
		}

		if (advancedDraft) {
			return (
				<div className="row no-margin g-2 advanced-stats-tab">
					<AdvancedStatsCards
						values={advancedDraft}
						onChange={(field, value) =>
							setAdvancedDraft((prev) => ({...prev, [field]: value}))
						}
					/>
				</div>
			);
		}

		if (advancedStatsLoaded && !advancedStats) {
			return (
				<div className="row no-margin g-1 advanced-stats-tab">
					<div className="col-sm-12 padding-small">
						<MatchEmpty
							text="Nessuna statistica di squadra per questa gara."
							action={
								matchData.canEditMatch && userRole !== "Player"
									? {
											icon: <EditIcon />,
											label: "Inserisci statistiche",
											onClick: startAdvancedStats,
										}
									: null
							}
						/>
					</div>
				</div>
			);
		}

		return (
			<div className="row no-margin g-2 advanced-stats-tab">
				<AdvancedStatsCards values={advancedStats} />
			</div>
		);
	};

	const tabs = [
		{
			key: "main_info",
			label: "Cronaca",
			render: () => {
				return (
					<div className="row no-margin">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Informazioni principali"
								content={
									<div className="row">
										<div className="col-sm-12">
											<div className="match-info-container">
												<div className="header-info-container">
													<div className="date-container">
														{matchData?.startDate
															? moment(matchData.startDate).format(
																	dateFormat +
																		(matchData.startTimeToBeConfirmed
																			? ""
																			: " HH:mm"),
																)
															: null}
														{matchData?.startTimeToBeConfirmed &&
															" · Orario da definire"}
													</div>
													<div className="competition-container">
														{competitionLink}
													</div>
												</div>
												<div className="match-body-container">
													<div className="main-info-container">
														<div className="match-scoreboard">
															<div className="match-scoreboard__team match-scoreboard__team--home">
																<span className="small">Casa</span>
																<div className="match-scoreboard__club-name">
																	{matchData?.homeClub}
																</div>
																{(() => {
																	const roster = internalTeams.find(
																		(r) => r.id === matchData?.homeRosterId,
																	);
																	const name =
																		matchData?.homeTeamName ||
																		roster?.name ||
																		roster?.playerCategory?.name;
																	return (
																		<div className="match-scoreboard__roster-name">
																			{name || "\u00A0"}
																		</div>
																	);
																})()}
															</div>
															<div className="match-scoreboard__result">
																{Number.isInteger(matchData?.homeClubGoals) &&
																Number.isInteger(matchData?.awayClubGoals) ? (
																	<>
																		<span className="match-scoreboard__goals">
																			{matchData.homeClubGoals}
																		</span>
																		<span className="match-scoreboard__separator">
																			:
																		</span>
																		<span className="match-scoreboard__goals">
																			{matchData.awayClubGoals}
																		</span>
																	</>
																) : (
																	<span className="match-scoreboard__vs">
																		vs
																	</span>
																)}
															</div>
															<div className="match-scoreboard__team match-scoreboard__team--away">
																<span className="small">Trasferta</span>
																<div className="match-scoreboard__club-name">
																	{matchData?.awayClub}
																</div>
																{(() => {
																	const roster = internalTeams.find(
																		(r) => r.id === matchData?.awayRosterId,
																	);
																	const name =
																		matchData?.awayTeamName ||
																		roster?.name ||
																		roster?.playerCategory?.name;
																	return (
																		<div className="match-scoreboard__roster-name">
																			{name || "\u00A0"}
																		</div>
																	);
																})()}
															</div>
														</div>
													</div>
													<div className="match-details-container">
														<div className="detail-container">
															<div className="label">Durata</div>
															<div className="value">
																{matchData?.sportingMinutes == null
																	? "Non definita"
																	: matchData.sportingMinutes + "′"}
															</div>
														</div>
														<div className="detail-container">
															<div className="label">Tempi</div>
															<div className="value">
																{sportingSummary(matchData?.sportingPeriods)}
															</div>
														</div>
														{matchData?.competitionSeasonGroupRoundName ? (
															<div className="detail-container">
																<div className="label">Turno</div>
																<div className="value">
																	{matchData.competitionSeasonGroupRoundName}
																</div>
															</div>
														) : null}
														<div className="detail-container">
															<div className="label">Stadio</div>
															<div className="value">
																{matchData?.fieldName || "—"}
															</div>
														</div>
														<div className="detail-container">
															<div className="label">Descrizione</div>
															<div className="value">
																{matchData?.description || "—"}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								}
								openable={false}
							/>
						</div>
						<ResultStatus
							result={matchData?.result}
							canEdit={
								matchData.canEditMatch &&
								!["Player", "Analyst"].includes(userRole)
							}
							onChoose={async (source, expected) => {
								try {
									await MatchAPI.setResultSource(id, {source, expected});
									await getMatch();
									return true;
								} catch (error) {
									handleNotification(
										error?.response?.data?.error || ERROR_OPERATION,
										"error",
									);
									return false;
								}
							}}
						/>
						<div className="col-sm-12 padding-small mt-2">
							<MatchChronology
								matchData={matchData}
								onDataCompletenessChange={async (side, complete) => {
									setLoading(true);
									try {
										await MatchAPI.setDataCompleteness(
											id,
											side === "home"
												? {homeComplete: complete}
												: {awayComplete: complete},
										);
										await getMatch();
									} catch (error) {
										handleNotification(
											error?.response?.data?.error || ERROR_OPERATION,
											"error",
										);
									} finally {
										setLoading(false);
									}
								}}
								onAddEvent={(type, col, players) => {
									setInitialData({
										type,
										allowUnknownScorer:
											["goal", "injury_exit", "substitution"].includes(type) &&
											col.clubId !== getInternalClubId(),
										matchSide: col.side,
										clubId: col.clubId,
										players,
										events: matchData?.matchEvents,
										allowReentry: matchData?.allowReentry,
										sportingPeriods: matchData?.sportingPeriods,
										participationComplete:
											col.side === "home"
												? matchData?.homeParticipationComplete
												: matchData?.awayParticipationComplete,
									});
									setOpenEventModal(true);
								}}
								onEditEvent={(event, col, players) => {
									setInitialData({
										event,
										type: [
											"goal",
											"own_goal",
											"penalty_scored",
											"penalty_missed",
										].includes(event.eventType)
											? "goal"
											: ["substitution", "injury_exit"].includes(
														event.eventType,
												  )
												? event.eventType
												: "sanction",
										matchSide: col.side,
										clubId: col.clubId,
										players,
										allowUnknownScorer:
											[
												"goal",
												"own_goal",
												"penalty_scored",
												"penalty_missed",
												"injury_exit",
												"substitution",
											].includes(event.eventType) &&
											col.clubId !== getInternalClubId(),
										events: matchData?.matchEvents,
										allowReentry: matchData?.allowReentry,
										sportingPeriods: matchData?.sportingPeriods,
										participationComplete:
											col.side === "home"
												? matchData?.homeParticipationComplete
												: matchData?.awayParticipationComplete,
									});
									setOpenEventModal(true);
								}}
								onDeleteEvent={(e) => {
									setEntityToDelete({type: "match_event", id: e.id});
									setOpenHandleConfirm(true);
								}}
							/>
						</div>
						{/* ── Video e Tagging ──────────────────── */}
						<MatchVideoTagging matchData={matchData} />

						{userRole !== "Player" && (
							<div className="col-sm-12 padding-small mt-2">
								<VLFCard
									title="Allegati"
									content={
										<AttachmentsContainer
											authenticatedImages
											entityId={id}
											entityType="match"
											attachments={attachments}
											handleEdit={
												!["Player", "Analyst"].includes(userRole)
													? editTacticalBoard
													: undefined
											}
											handleAdd={() => getMatchAttachment()}
											handleDownload={handleDownload}
											handleDelete={(id) => {
												setEntityToDelete({type: "attachment", id});
												setOpenHandleConfirm(true);
											}}
											handleNotification={handleNotification}
										/>
									}
								/>
							</div>
						)}
						{userRole !== "Player" && isInternalMatch && (
							<div className="col-sm-12 padding-small">
								<NotesSection
									type="match"
									notes={matchNotes}
									handleConfirm={handleNote}
									handleDelete={deleteNote}
								/>
							</div>
						)}
					</div>
				);
			},
		},
		{
			key: "match_convocated_players",
			label: t("convocations"),
			hidden:
				!isInternalMatch || userRole === "Player" || userRole === "Analyst",
			render: () => (
				<ConvocationsTab
					matchData={matchData}
					teamPlayers={teamPlayers}
					onAddPlayers={(matchSide) => {
						setInitialData({matchId: id, matchSide, status: "Convocato"});
						setOpenAttendanceModal(true);
					}}
					onStatusClick={handleInlineAttendance}
					injuredIds={injuredIds}
				/>
			),
		},
		{
			key: "match_report_players",
			label: "Distinte",
			hidden: userRole === "Player",
			render: () => (
				<fieldset disabled={!matchData.canEditMatch} className="match-fieldset">
					<DistinteTab
						matchData={matchData}
						clubs={clubs}
						onConfirmRoster={confirmRosterPlayers}
						onUpdatePlayer={saveMatchPlayer}
						onAssignPlayer={async (p, matchSide) => {
							try {
								await MatchAPI.updatePlayer(id, p.id, {id: p.id, matchSide});
								getMatch();
							} catch (error) {
								handleNotification(
									error?.response?.data?.error || ERROR_OPERATION,
									"error",
								);
							}
						}}
						onAddPlayer={(col, isInternal) => {
							setInitialData({
								matchId: id,
								matchCategoryId: matchData?.playersCategoryId,
								matchCategoryLabel: matchData?.playersCategory,
								matchRosterId: col.rosterId,
								matchSide: col.side,
								clubId: col.clubId,
								isInternal,
							});
							setOpenReportModal(true);
						}}
						onSwapPlayer={(p) => {
							if (!p.provisional) {
								handleMatchPlayerUpdate(p);
								return;
							}
							setInitialData({
								linkParticipationId: p.id,
								playerNumber: p.playerNumber,
								playerName: p.playerName,
								matchRosterId:
									p.matchSide === "home"
										? matchData.homeRosterId
										: matchData.awayRosterId,
								matchSide: p.matchSide,
								clubId: p.clubId,
								isInternal: false,
							});
							setOpenReportModal(true);
						}}
						onDeletePlayer={(p) => {
							setEntityToDelete({type: "match_player", id: p.id});
							setOpenHandleConfirm(true);
						}}
						onDownloadPDF={handleDownloadLineupPDF}
					/>
				</fieldset>
			),
		},
		{
			key: "scouting",
			label: "Scouting",
			hidden: !matchData.canReadScouting || userRole === "Player",
			render: () => (
				<MatchScouting
					matchData={matchData}
					onRefresh={getMatch}
					handleNotification={handleNotification}
				/>
			),
		},
		{
			key: "tactical_board",
			label: "Lavagna tattica",
			hidden: userRole === "Player",
			render: () => (
				<TacticalBoardPanel
					key={id}
					entityType="match"
					entityId={id}
					attachments={attachments}
					onRefresh={getMatchAttachment}
					selectedId={selectedBoardId}
					onSelect={setSelectedBoardId}
					editable={
						matchData.canEditMatch && !["Player", "Analyst"].includes(userRole)
					}
					onDelete={(attachmentId) => {
						setEntityToDelete({type: "attachment", id: attachmentId});
						setOpenHandleConfirm(true);
					}}
				/>
			),
		},
		{
			key: "analysis",
			label: "Analisi",
			hidden: userRole === "Player",
			render: () =>
				!analysisEditing && !isAnalysisSaved ? (
					<MatchEmpty
						text="Nessuna analisi per questa gara."
						action={
							matchData.canEditMatch
								? {
										icon: <EditIcon />,
										label: "Scrivi analisi",
										onClick: () => setAnalysisEditing(true),
									}
								: null
						}
					/>
				) : (
					<div
						className={`match-analysis${analysisEditing ? " match-analysis--editing" : ""}`}
					>
						{[true, false].map((first) => (
							<section
								className="match-analysis-section"
								key={first ? "first" : "second"}
								aria-label={
									first ? "Analisi primo tempo" : "Analisi secondo tempo"
								}
							>
								<header className="match-analysis-section__header">
									<h3>{first ? "1° tempo" : "2° tempo"}</h3>
								</header>
								<div className="match-analysis-section__body">
									{getFractionField(first)}
								</div>
							</section>
						))}
						<section
							className="match-analysis-section"
							aria-label="Relazione generale"
						>
							<header className="match-analysis-section__header">
								<h3>Relazione generale</h3>
							</header>
							<div className="match-analysis-section__body">
								{getTextAreaField({
									values: analysisFields,
									setValues: setAnalysisFields,
									fieldKey: "generalTeamReport",
									minRows: 4,
									maxRows: 12,
									editable: analysisEditing,
								})}
							</div>
						</section>
					</div>
				),
		},
		{
			key: "rpe",
			label: "RPE",
			hidden: !isInternalMatch || !isMatchCategoryPro || userRole === "Analyst",
			render: () => {
				if (userRole === "Player") {
					return (
						<div className="row no-margin rpe-tab-container g-2">
							<div
								key="convocation"
								className="col-sm-12 col-lg-6 padding-small"
							>
								<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100">
									<div className="choice-label">
										Hai partecipato alla partita?
									</div>
									<div className="choice-options">
										{attendanceStatusOptions.map((as, k) => (
											<div
												key={k}
												className={
													"single-option" +
													(attendance === as.value ? " selected" : "")
												}
												onClick={() => setAttendance(as.value)}
											>
												{as.label}
											</div>
										))}
									</div>
								</div>
							</div>
							<div key="rpe" className="col-sm-12 col-lg-6 padding-small">
								<div className="player-choice d-flex flex-column align-items-center justify-content-center h-100 lh-1">
									<div className="choice-label">RPE</div>
									<Rating
										selectedRating={formData.rpe}
										setSelectedRating={(value) =>
											setFormData({...formData, rpe: value})
										}
										max={10}
										min={1}
									/>
									<div>
										{
											rpeOptions.filter(
												(o) => o.value === formData.rpe?.toString(),
											)?.[0]?.label
										}
									</div>
								</div>
							</div>
						</div>
					);
				}

				return (
					<RpeTab
						matchData={matchData}
						teamPlayers={teamPlayers}
						onEdit={(mr) => {
							setInitialData({
								matchCategoryId: matchData?.playersCategoryId,
								matchCategoryLabel: matchData?.playersCategory,
								...mr,
							});
							setOpenRPEModal(true);
						}}
						onDelete={(mr) => {
							setEntityToDelete({type: "rpe", id: mr.id});
							setOpenHandleConfirm(true);
						}}
					/>
				);
			},
		},
		{
			key: "gps",
			label: "GPS",
			hidden:
				!isInternalMatch ||
				!isMatchCategoryPro ||
				userRole === "Analyst" ||
				userRole === "Player",
			render: () => (
				<GpsStatsTab
					matchId={id}
					players={teamPlayers}
					handleNotification={handleNotification}
				/>
			),
		},
		{
			key: "advanced_stats",
			label: "Squadra",
			hidden: userRole === "Player",
			render: () => renderAdvancedStatsTab(),
		},
		{
			key: "stats",
			label: "Giocatori",
			hidden: userRole === "Player",
			render: () => (
				<fieldset disabled={!matchData.canEditMatch} className="match-fieldset">
					<PlayerStatsTab
						matchData={matchData}
						teamPlayers={teamPlayers}
						isMatchCategoryPro={isMatchCategoryPro}
						matchId={id}
						userRole={userRole}
						onSaveSources={async (row, sources, expected) => {
							try {
								await MatchAPI.savePlayerStatistics(id, row.participationId, {
									sources,
									expected,
								});
								await getMatch();
								return true;
							} catch (error) {
								handleNotification(
									error?.response?.data?.error || ERROR_OPERATION,
									"error",
								);
								return false;
							}
						}}
						onEditStat={(me) => {
							setInitialData({
								id: me.id,
								playerId: me.playerId,
								minutes: me.minutes,
								goalsConceded: me.goalsConceded,
								attendances: matchData?.matchConvocations,
								hasRPE: isMatchCategoryPro,
								...me,
							});
							setOpenModal(true);
						}}
						onDeleteStat={(me) => {
							setEntityToDelete({
								type: "evaluation",
								id: me.id,
								participationId: me.participationId,
							});
							setOpenHandleConfirm(true);
						}}
						onPlayerClick={async (playerId) => {
							try {
								const player = await PlayerAPI.getById(playerId, {seasonId});
								navigate(
									player.isInternal
										? "/players/" + playerId
										: "/scouting/players/" + playerId,
								);
							} catch {
								handleNotification(ERROR_OPERATION, "error");
							}
						}}
						onReport={openGoalkeeperReport}
						onEmptyAction={
							userRole !== "Player"
								? () => dispatch(setMatchTab("match_report_players"))
								: undefined
						}
					/>
				</fieldset>
			),
		},
	];

	const visibleSections = tabs
		.filter((tab) => !tab.hidden)
		.map((tab) =>
			tab.key === currentTab && tabActions.length
				? {
						...tab,
						render: () => (
							<>
								<div className="main-actions-bar">
									<MainActions actions={tabActions} />
								</div>
								{tab.render()}
							</>
						),
					}
				: tab,
		);
	const selectedTab =
		visibleSections.find((tab) => tab.key === currentTab)?.key || "main_info";
	const momentTabs = MATCH_MOMENTS.map((moment) => {
		const sections = moment.sections
			.map((key) => visibleSections.find((tab) => tab.key === key))
			.filter(Boolean);
		return {
			key: moment.key,
			label: moment.label,
			hidden: !sections.length,
			firstSection: sections[0]?.key,
			render: () => (
				<MatchMoment
					sections={sections}
					current={selectedTab}
					onChange={(key) => dispatch(setMatchTab(key))}
				/>
			),
		};
	});
	const selectedMoment = MATCH_MOMENTS.find((m) =>
		m.sections.includes(selectedTab),
	)?.key;

	return (
		<Page className="page-container">
			<Loading visible={loading || loadingAdvancedStats} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					matchData?.startDate
						? matchData.homeClub + " - " + matchData.awayClub
						: ""
				}
				actions={
					<MainActions
						actions={
							matchData.canEditMatch
								? mainActions
								: mainActions.filter(
										(a) => a.title === "Indietro" || a.title === "PDF",
									)
						}
					/>
				}
			/>
			<Tabs
				tabs={momentTabs}
				currentTab={selectedMoment}
				setCurrentTab={(key) =>
					key !== selectedMoment &&
					dispatch(
						setMatchTab(momentTabs.find((m) => m.key === key)?.firstSection),
					)
				}
			/>

			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => {
					switch (entityToDelete?.type) {
						case "attachment":
							return deleteAttachment();
						case "attendance":
							return deleteAttendance();
						case "evaluation":
							return deleteEvaluation();
						case "gps":
							return;
						case "match":
							return deleteMatch();
						case "match_event":
							return deleteMatchEvent();
						case "match_player":
							return deleteMatchPlayer();
						case "rpe":
							return deleteMeasurement();
						default:
							return;
					}
				}}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
			{openModal ? (
				<HandleStatModal
					open={openModal}
					confirm={handleStat}
					close={handleClose}
					initialData={initialData}
					players={teamPlayers}
				/>
			) : null}
			{openAttendanceModal ? (
				<HandleAttendanceModal
					open={openAttendanceModal}
					confirm={handleAttendance}
					close={handleClose}
					existingPlayerIds={(matchData?.matchConvocations || [])
						.filter((c) => c.matchSide === initialData.matchSide)
						.map((c) => c.playerId)}
					initialData={initialData}
				/>
			) : null}
			{openEventModal ? (
				<HandleEventModal
					open={openEventModal}
					confirm={handleEvent}
					close={handleClose}
					initialData={initialData}
				/>
			) : null}
			{openReportModal ? (
				<HandleReportModal
					excludedPlayerIds={
						initialData?.isInternal
							? [
									...(matchData?.matchPlayers || [])
										.filter(
											(p) =>
												p.matchSide === initialData.matchSide ||
												(!p.matchSide &&
													matchData.homeClubId !== matchData.awayClubId &&
													p.clubId === initialData.clubId),
										)
										.map((p) => p.playerId),
									...(matchData?.matchConvocations || [])
										.filter(
											(c) =>
												c.matchSide === initialData.matchSide &&
												["Assente", "Infortunato"].includes(c.status),
										)
										.map((c) => c.playerId),
								]
							: []
					}
					open={openReportModal}
					confirm={handleReport}
					close={handleClose}
					initialData={initialData}
					players={
						initialData?.isInternal
							? teamPlayers?.filter(
									(p) =>
										!matchData?.matchPlayers?.find(
											(r) =>
												r.playerId === p.id &&
												(r.matchSide === initialData?.matchSide ||
													(!r.matchSide &&
														matchData?.homeClubId !== matchData?.awayClubId)),
										) &&
										!matchData?.matchConvocations?.some(
											(c) =>
												c.playerId === p.id &&
												c.matchSide === initialData?.matchSide &&
												["Assente", "Infortunato"].includes(c.status),
										),
								)
							: prospectPlayers?.filter(
									(pp) =>
										!matchData?.matchPlayers?.find(
											(r) =>
												r.playerId === pp.id &&
												r.matchSide === initialData?.matchSide,
										),
								)
					}
				/>
			) : null}
			{openRPEModal ? (
				<HandleRPEModal
					open={openRPEModal}
					confirm={handleMeasurement}
					close={handleClose}
					initialData={initialData}
					rosterPlayerIds={matchRosterPlayerIds}
					players={(teamPlayers || []).filter((p) =>
						initialData?.id
							? p.id === initialData?.playerId
							: !matchData?.matchRpe?.find((r) => r.playerId === p.id),
					)}
				/>
			) : null}
			<VLFModal
				open={Boolean(injuryWarning)}
				title="Giocatore infortunato"
				content={
					<div>
						{injuryWarning}: infortunio attivo alla data dell’evento. Vuoi
						confermare la presenza?
					</div>
				}
				confirm={() => resolveInjuryWarning(true)}
				close={() => resolveInjuryWarning(false)}
				confirmLabel="Conferma presenza"
			/>
		</Page>
	);
}

export default MatchPage;
